import { currentConfig } from "../config/environment";
import { getErrorMessage } from "../utils/errorMessage";

export interface ServiceStatus {
  name: string;
  status: "healthy" | "unhealthy" | "checking";
  url: string;
  responseTime?: number;
  lastChecked?: Date;
  errorMessage?: string;
  version?: string;
  /**
   * When true this service is a keep-alive ping target (DEV only).
   * Its status is shown for visibility but is excluded from overallStatus
   * so a sleeping Render service doesn't alarm users on primary health.
   */
  keepAliveOnly?: boolean;
}

export interface HealthCheckResponse {
  services: ServiceStatus[];
  overallStatus: "healthy" | "degraded" | "unhealthy";
  lastUpdated: Date;
}

interface ServiceConfig {
  name: string;
  /** Origin URL — shown in ServiceStatus.url and used for display. */
  baseUrl: string;
  /** Full URL used for the actual ping request (may include a specific path). */
  pingUrl: string;
  /** If true, excluded from overallStatus calculation */
  keepAliveOnly?: boolean;
}

/**
 * Title served by Render.com while a free-tier service is cold-starting.
 * Mirrors the shell-script check: if the page title matches this string the
 * service is still waking up, not yet ready to handle real traffic.
 */
const RENDER_LOADING_TITLE = "Render - Application loading";

class HealthCheckService {
  private readonly services: ServiceConfig[];

  constructor() {
    const primary: ServiceConfig[] = [
      {
        name: "User Management",
        baseUrl: currentConfig.apiBaseUrl,
        pingUrl: `${currentConfig.apiBaseUrl}/health`,
      },
      {
        name: "CTMS Core",
        baseUrl: currentConfig.coreApiBaseUrl,
        pingUrl: `${currentConfig.coreApiBaseUrl}/health`,
      },
    ];

    // Keep-alive services come entirely from VITE_KEEP_ALIVE_URLS — no
    // environment-specific logic here. Each entry carries the full ping URL
    // (including path) exactly as listed in the wake-up script.
    const keepAlive: ServiceConfig[] = currentConfig.keepAliveUrls.map(
      ({ name, url }) => {
        let baseUrl = url;
        try {
          baseUrl = new URL(url).origin;
        } catch {
          /* leave as-is if URL is malformed */
        }
        return { name, baseUrl, pingUrl: url, keepAliveOnly: true };
      },
    );

    this.services = [...primary, ...keepAlive];
  }

  /**
   * Check the health of a single service.
   *
   * Mirrors the shell wake-up script logic:
   *  1. Fetch the service's designated ping URL.
   *  2. If the response body contains RENDER_LOADING_TITLE the service is still
   *     cold-starting — report as unhealthy with "Waking up…" message.
   *  3. Any other successful response → healthy.
   *  4. On CORS failure fall back to a no-cors ping — an opaque response still
   *     proves the server is awake and keeps Render alive.
   *  5. Network error / timeout on both attempts → unhealthy.
   */
  private async checkServiceHealth(
    service: ServiceConfig,
  ): Promise<ServiceStatus> {
    const startTime = Date.now();

    // --- Attempt 1: CORS-enabled fetch (full visibility) ---
    try {
      const response = await fetch(service.pingUrl, {
        method: "GET",
        signal: AbortSignal.timeout(15000),
      });

      const responseTime = Date.now() - startTime;

      // Detect Render cold-start loading page (HTML title check)
      const contentType = response.headers.get("content-type") ?? "";
      if (contentType.includes("text/html")) {
        const text = await response.text();
        if (text.includes(RENDER_LOADING_TITLE)) {
          return {
            name: service.name,
            status: "unhealthy",
            url: service.baseUrl,
            responseTime,
            lastChecked: new Date(),
            errorMessage: "Waking up…",
            keepAliveOnly: service.keepAliveOnly,
          };
        }
      }

      return {
        name: service.name,
        status: response.ok ? "healthy" : "unhealthy",
        url: service.baseUrl,
        responseTime,
        lastChecked: new Date(),
        version: response.headers.get("x-api-version") ?? undefined,
        keepAliveOnly: service.keepAliveOnly,
      };
    } catch {
      // --- Attempt 2: no-cors keep-alive ping ---
      // An opaque response (status 0) still wakes the Render service.
      try {
        await fetch(service.pingUrl, {
          method: "GET",
          mode: "no-cors",
          signal: AbortSignal.timeout(15000),
        });

        const responseTime = Date.now() - startTime;
        return {
          name: service.name,
          // Opaque response = server is awake and responding
          status: "healthy",
          url: service.baseUrl,
          responseTime,
          lastChecked: new Date(),
          keepAliveOnly: service.keepAliveOnly,
        };
      } catch (networkError: unknown) {
        const responseTime = Date.now() - startTime;
        return {
          name: service.name,
          status: "unhealthy",
          url: service.baseUrl,
          responseTime,
          lastChecked: new Date(),
          errorMessage: getErrorMessage(networkError, "Service unreachable"),
          keepAliveOnly: service.keepAliveOnly,
        };
      }
    }
  }

  /**
   * Check the health of all configured services.
   *
   * overallStatus is derived only from primary (non-keepAliveOnly) services so
   * that sleeping DEV Render instances do not alarm users about core health.
   */
  async checkAllServices(): Promise<HealthCheckResponse> {
    const serviceChecks = this.services.map((service) =>
      this.checkServiceHealth(service),
    );
    const services = await Promise.all(serviceChecks);

    // Only primary services affect the overallStatus indicator
    const primaryServices = services.filter((s) => !s.keepAliveOnly);
    const healthyCount = primaryServices.filter(
      (s) => s.status === "healthy",
    ).length;
    const totalCount = primaryServices.length;

    let overallStatus: "healthy" | "degraded" | "unhealthy";
    if (healthyCount === totalCount) {
      overallStatus = "healthy";
    } else if (healthyCount > 0) {
      overallStatus = "degraded";
    } else {
      overallStatus = "unhealthy";
    }

    return {
      services,
      overallStatus,
      lastUpdated: new Date(),
    };
  }

  /**
   * Get the status of services from cache or perform fresh check
   */
  async getServicesStatus(): Promise<HealthCheckResponse> {
    // For now, always perform fresh check
    // In a production app, you might want to implement caching here
    return this.checkAllServices();
  }

  /**
   * Get the list of configured services
   */
  getConfiguredServices(): { name: string; baseUrl: string }[] {
    return this.services.map((service) => ({
      name: service.name,
      baseUrl: service.baseUrl,
    }));
  }
}

// Export singleton instance
export const healthCheckService = new HealthCheckService();
